// src/components/admin/Views/Series/AddSeries.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MEDIA_BASE } from '../../../../api/config';

import '../../../../assets/styles/add-series.scss';

import {
    createSeries,
    getSeries,
    updateSeries,
    listSeriesContents,
    addSeriesContents,
    removeSeriesItem,
    updateSeriesItemOrder,
    uploadSeriesCover,
    deleteSeriesCover,
    type SeriesItem,
    listSeriesTranslations,
    upsertSeriesTranslation,
    patchSeriesTranslation,
    deleteSeriesTranslation,
    type SeriesTranslation,
} from '../../../../api/series';

import { getAllContentsDetails } from '../../../../api/contents';

/* ================== Dialogi ================== */

function Dialog({
    open,
    title,
    message,
    onClose,
    variant = 'ok',
}: {
    open: boolean;
    title?: string;
    message?: string;
    variant?: 'ok' | 'error';
    onClose: () => void;
}) {
    if (!open) return null;
    return (
        <div className="modal-backdrop">
            <div className={`modal-card ${variant === 'error' ? 'modal-error' : ''}`}>
                {title && <h3>{title}</h3>}
                {message && <p style={{ whiteSpace: 'pre-wrap' }}>{message}</p>}
                <div className="modal-actions">
                    <button className="btn-primary" onClick={onClose}>OK</button>
                </div>
            </div>
        </div>
    );
}

function ConfirmDialog({
    open,
    title = 'Potwierdź',
    message,
    confirmLabel = 'Potwierdź',
    cancelLabel = 'Anuluj',
    danger = false,
    onConfirm,
    onCancel,
}: {
    open: boolean;
    title?: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    danger?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}) {
    if (!open) return null;
    return (
        <div className="modal-backdrop">
            <div className="modal-card">
                {title && <h3>{title}</h3>}
                {message && <p style={{ whiteSpace: 'pre-wrap' }}>{message}</p>}
                <div className="modal-actions">
                    <button className="btn-ghost" onClick={onCancel}>{cancelLabel}</button>
                    <button className={danger ? 'delete-btn' : 'btn-primary'} onClick={onConfirm}>{confirmLabel}</button>
                </div>
            </div>
        </div>
    );
}

/* ================== Typy lokalne ================== */

type FormData = {
    title: string;
    description: string;
    language: string;
    is_visible: boolean;
    is_premium: boolean;
    cover_image_url: string; // opcjonalny zewnętrzny URL
};

type ContentBrief = {
    id: number;
    title: string;
    type: string;
    language: string | null;
    is_visible: boolean;
    is_premium: boolean;
};

/* ================== Utils ================== */

function lastPathSegment(href: string | null | undefined): string | null {
    if (!href) return null;
    try {
        const noQuery = href.split('?')[0];
        const seg = noQuery.split('/').pop();
        return seg || null;
    } catch {
        return null;
    }
}

function resolveImageUrl(url: string | null): string | null {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${MEDIA_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
}

/* ====== Tłumaczenia: typy i helper ====== */
type Lang = 'PL' | 'EN' | 'ES' | 'DE' | 'UK';
const ALLOWED: Lang[] = ['PL', 'EN', 'ES', 'DE', 'UK'];
const ensureLang = (v: string | null | undefined): Lang => {
    const u = String(v || '').toUpperCase();
    return (ALLOWED as string[]).includes(u) ? (u as Lang) : 'PL';
};

/* ================== Główny komponent ================== */

export default function AddSeries() {
    const navigate = useNavigate();
    const { id } = useParams();
    const seriesIdFromRoute = id ? parseInt(id) : null;

    const [seriesId, setSeriesId] = useState<number | null>(seriesIdFromRoute);
    const [locked, setLocked] = useState(seriesIdFromRoute != null);
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState<FormData>({
        title: '',
        description: '',
        language: '',
        is_visible: false,
        is_premium: false,
        cover_image_url: '',
    });

    // okładka
    const [coverUrl, setCoverUrl] = useState<string | null>(null);
    const [coverFileName, setCoverFileName] = useState<string | null>(null);
    const coverInputRef = useRef<HTMLInputElement | null>(null);

    // lista elementów serii
    const [items, setItems] = useState<SeriesItem[]>([]);

    // dialogi
    const [dlgOpen, setDlgOpen] = useState(false);
    const [dlgMsg, setDlgMsg] = useState('');
    const [dlgTitle, setDlgTitle] = useState<string | undefined>();
    const [dlgVariant, setDlgVariant] = useState<'ok' | 'error'>('ok');

    const [confirmState, setConfirmState] = useState<{
        open: boolean;
        title?: string;
        message?: string;
        confirmLabel?: string;
        cancelLabel?: string;
        danger?: boolean;
        onConfirm?: () => void;
    }>({ open: false });

    const showOk = (msg: string, title = 'Sukces') => { setDlgVariant('ok'); setDlgTitle(title); setDlgMsg(msg); setDlgOpen(true); };
    const showErr = (msg: string, title = 'Błąd') => { setDlgVariant('error'); setDlgTitle(title); setDlgMsg(msg); setDlgOpen(true); };

    // ===== TŁUMACZENIA OPISU (proste) =====
    const [translations, setTranslations] = useState<SeriesTranslation[]>([]);
    const [trOpen, setTrOpen] = useState<Record<Lang, boolean>>({ PL: false, EN: false, ES: false, DE: false, UK: false });

    const [trAddOpen, setTrAddOpen] = useState(false);
    const [trLang, setTrLang] = useState<Lang>('PL');
    const [trDesc, setTrDesc] = useState<string>('');
    const [trVisible, setTrVisible] = useState<boolean>(false);

    const [trEditLang, setTrEditLang] = useState<Lang | null>(null);
    const [trEditDesc, setTrEditDesc] = useState<string>('');

    const toggleTrOpen = (lang: Lang) => setTrOpen(s => ({ ...s, [lang]: !s[lang] }));

    const refreshTranslations = async () => {
        if (!seriesId) return;
        const trs = await listSeriesTranslations(seriesId);
        setTranslations(trs);
    };

    const handleAddTranslation = async () => {
        if (!seriesId) return;
        try {
            await upsertSeriesTranslation(seriesId, trLang, {
                translated_description: trDesc || null,
                is_visible: trVisible,
            });
            await refreshTranslations();
            setTrAddOpen(false);
            setTrDesc('');
            setTrVisible(false);
            showOk('Dodano/zmieniono tłumaczenie opisu.');
        } catch (e: any) {
            showErr(e?.error || 'Błąd przy dodawaniu tłumaczenia.');
        }
    };

    const startEditTr = (lang: Lang, currentDesc: string | null) => {
        setTrEditLang(lang);
        setTrEditDesc(currentDesc || '');
    };

    const saveEditTr = async () => {
        if (!seriesId || !trEditLang) return;
        try {
            await patchSeriesTranslation(seriesId, trEditLang, { translated_description: trEditDesc });
            await refreshTranslations();
            setTrEditLang(null);
            setTrEditDesc('');
            showOk('Zapisano tłumaczenie.');
        } catch (e: any) {
            showErr(e?.error || 'Błąd przy zapisie tłumaczenia.');
        }
    };

    const toggleTrVisibility = async (lang: Lang, cur?: boolean) => {
        if (!seriesId) return;
        try {
            await patchSeriesTranslation(seriesId, lang, { is_visible: !cur });
            await refreshTranslations();
            showOk('Zmieniono widoczność tłumaczenia.');
        } catch (e: any) {
            showErr(e?.error || 'Błąd przy zmianie widoczności.');
        }
    };

    const removeTranslation = async (lang: Lang) => {
        if (!seriesId) return;
        setConfirmState({
            open: true,
            title: `Usunąć tłumaczenie ${lang}?`,
            message: 'Operacji nie można cofnąć.',
            confirmLabel: 'Usuń',
            cancelLabel: 'Anuluj',
            danger: true,
            onConfirm: async () => {
                try {
                    await deleteSeriesTranslation(seriesId, lang);
                    await refreshTranslations();
                    showOk('Tłumaczenie usunięte.');
                } catch (e: any) {
                    showErr(e?.error || 'Błąd przy usuwaniu tłumaczenia.');
                } finally {
                    setConfirmState(s => ({ ...s, open: false }));
                }
            }
        });
    };

    // ===== INIT: edycja =====
    useEffect(() => {
        if (!seriesIdFromRoute) return;
        (async () => {
            try {
                const s = await getSeries(seriesIdFromRoute);
                setForm({
                    title: s.title || '',
                    description: (s.description as string) || '',
                    language: (s.language as string) || '',
                    is_visible: s.is_visible ?? false,
                    is_premium: s.is_premium ?? false,
                    cover_image_url: (s.cover_image_url as string) || '',
                });

                const url = (s.cover_url as string) || (s.cover_image_url as string) || null;
                setCoverUrl(url);
                setCoverFileName(lastPathSegment(url));

                const it = await listSeriesContents(seriesIdFromRoute);
                setItems(it.sort((a, b) => a.sort_index - b.sort_index));

                const trs = await listSeriesTranslations(seriesIdFromRoute);
                setTranslations(trs);
            } catch (e: any) {
                showErr(e?.message || 'Nie udało się wczytać serii.');
            }
        })();
    }, [seriesIdFromRoute]);

    // ===== Zmiana pól formularza =====
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            setForm(f => ({ ...f, [name]: (e.target as HTMLInputElement).checked }));
        } else {
            setForm(f => ({ ...f, [name]: value }));
        }
    };

    // ===== Utworzenie serii =====
    const handleCreateSeries = async (e: React.FormEvent) => {
        e.preventDefault();
        if (seriesId) return;
        setLoading(true);
        try {
            const created = await createSeries({
                title: form.title,
                description: form.description || null,
                language: form.language || null,
                is_visible: !!form.is_visible,
                is_premium: !!form.is_premium,
                cover_image_url: form.cover_image_url || null,
            });
            setSeriesId(created.id);
            if (created.cover_image_url) {
                setCoverUrl(created.cover_image_url as string);
                setCoverFileName(lastPathSegment(created.cover_image_url as string));
            }
            showOk('Seria utworzona! Teraz możesz dodać do niej treści.');
        } catch (e: any) {
            showErr(e?.error || e?.message || 'Błąd przy tworzeniu serii.');
        } finally {
            setLoading(false);
        }
    };

    // ===== Aktualizacja serii =====
    const handleUpdateSeries = async () => {
        if (!seriesId) return;
        setLoading(true);
        try {
            const updated = await updateSeries(seriesId, {
                title: form.title,
                description: form.description || null,
                language: form.language || null,
                is_visible: !!form.is_visible,
                is_premium: !!form.is_premium,
                cover_image_url: form.cover_image_url || null,
            });
            if (updated.cover_image_url) {
                setCoverUrl(updated.cover_image_url as string);
                setCoverFileName(lastPathSegment(updated.cover_image_url as string));
            }
            showOk('Zmiany zapisane.');
        } catch (e: any) {
            showErr(e?.error || e?.message || 'Błąd przy zapisie zmian.');
        } finally {
            setLoading(false);
        }
    };

    /* ================== Dodawanie treści do serii ================== */

    const [allContents, setAllContents] = useState<ContentBrief[]>([]);
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState<ContentBrief[]>([]);
    const debounceRef = useRef<number | undefined>(undefined);

    useEffect(() => {
        (async () => {
            try {
                const rows = await getAllContentsDetails();
                const brief: ContentBrief[] = rows.map((r: any) => ({
                    id: r.id,
                    title: r.title,
                    type: r.type,
                    language: r.language ?? null,
                    is_visible: !!r.is_visible,
                    is_premium: !!r.is_premium,
                }));
                setAllContents(brief);
            } catch {
                // opcjonalne
            }
        })();
    }, []);

    useEffect(() => {
        if (debounceRef.current) window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(() => {
            const q = query.trim().toLowerCase();
            if (q.length < 2) {
                setSuggestions([]);
                return;
            }
            const out = allContents
                .filter(c => String(c.id).includes(q) || c.title.toLowerCase().includes(q))
                .slice(0, 20);
            setSuggestions(out);
        }, 250) as unknown as number;

        return () => {
            if (debounceRef.current) window.clearTimeout(debounceRef.current);
        };
    }, [query, allContents]);

    const [selected, setSelected] = useState<ContentBrief[]>([]);

    const addToSelection = (c: ContentBrief) => {
        setSelected(prev => (prev.find(x => x.id === c.id) ? prev : [...prev, c]));
        setQuery('');
        setSuggestions([]);
    };

    const removeFromSelection = (id: number) => {
        setSelected(prev => prev.filter(s => s.id !== id));
    };

    const addSelectedToSeries = async () => {
        if (!seriesId || selected.length === 0) return;
        try {
            await addSeriesContents(seriesId, selected.map(s => s.id));
            const it = await listSeriesContents(seriesId);
            setItems(it.sort((a, b) => a.sort_index - b.sort_index));
            setSelected([]);
            showOk('Dodano treści do serii.');
        } catch (e: any) {
            showErr(e?.error || e?.message || 'Nie udało się dodać treści do serii.');
        }
    };

    /* ================== Kolejność / usuwanie ================== */

    const sortedItems = useMemo(
        () => [...items].sort((a, b) => a.sort_index - b.sort_index),
        [items]
    );

    const moveItem = async (contentId: number, dir: -1 | 1) => {
        if (!seriesId) return;
        const idx = sortedItems.findIndex(i => i.content_id === contentId);
        const j = idx + dir;
        if (idx < 0 || j < 0 || j >= sortedItems.length) return;
        const A = sortedItems[idx];
        const B = sortedItems[j];

        try {
            await updateSeriesItemOrder(seriesId, A.content_id, B.sort_index);
            await updateSeriesItemOrder(seriesId, B.content_id, A.sort_index);

            setItems(prev => prev.map(p => {
                if (p.content_id === A.content_id) return { ...p, sort_index: B.sort_index };
                if (p.content_id === B.content_id) return { ...p, sort_index: A.sort_index };
                return p;
            }));
        } catch (e: any) {
            showErr(e?.message || 'Nie udało się zmienić kolejności.');
        }
    };

    const askRemoveItem = (it: SeriesItem) => {
        if (!seriesId) return;
        setConfirmState({
            open: true,
            title: 'Usunąć element z serii?',
            message: `„${it.title}” (ID ${it.content_id}) zostanie odłączony od tej serii.`,
            confirmLabel: 'Usuń',
            cancelLabel: 'Anuluj',
            danger: true,
            onConfirm: async () => {
                try {
                    await removeSeriesItem(seriesId, it.content_id);
                    setItems(prev => prev.filter(p => p.content_id !== it.content_id));
                    showOk('Usunięto z serii.');
                } catch (e: any) {
                    showErr(e?.message || 'Nie udało się usunąć elementu.');
                } finally {
                    setConfirmState(s => ({ ...s, open: false }));
                }
            }
        });
    };

    /* ================== Okładka — upload / delete ================== */

    const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !seriesId) return;
        const file = e.target.files[0];
        try {
            const res = await uploadSeriesCover(seriesId, file); // { image: 'https://...' lub '/media/...'}
            const url = res?.image as string;
            setCoverUrl(url);
            setCoverFileName(lastPathSegment(url) || file.name);
            showOk('Okładka dodana!');
        } catch (err: any) {
            showErr(err?.error || 'Błąd przy uploadzie okładki.');
        }
    };

    const handleDeleteCover = async () => {
        if (!seriesId || !coverFileName) return;
        try {
            await deleteSeriesCover(seriesId, coverFileName);
            setCoverUrl(null);
            setCoverFileName(null);
            showOk('Okładka usunięta.');
        } catch (err: any) {
            showErr(err?.error || 'Błąd przy usuwaniu okładki.');
        }
    };

    /* ================== Widoki ================== */

    const inputProps = locked ? { disabled: true } : {};
    const coverDisplay = resolveImageUrl(coverUrl || form.cover_image_url || null);

    return (
        <>
            <div className="contents-add-container">
                <div className="breadcrumb">
                    <span onClick={() => navigate('/admin')} className="breadcrumb-link">Dashboard</span> /
                    <span onClick={() => navigate('/admin/series')} className="breadcrumb-link">Serie</span> /
                    <span>{seriesIdFromRoute ? 'Edytuj serię' : 'Dodaj serię'}</span>
                </div>

                <div className="title-bar">
                    <h2>{seriesIdFromRoute ? 'Edytuj serię' : 'Dodaj serię'}</h2>
                    <div className='edit-buttons'>
                        <div className="form-buttons">
                            {seriesIdFromRoute == null ? (
                                <button form="series-form" type="submit" disabled={loading}>
                                    {loading ? 'Dodaję...' : 'Dodaj serię'}
                                </button>
                            ) : (
                                <button onClick={handleUpdateSeries} type="button" disabled={loading || locked}>
                                    {loading ? 'Zapisuję...' : 'Zapisz zmiany'}
                                </button>
                            )}
                        </div>
                        {seriesIdFromRoute !== null ?
                            <button
                                onClick={() => setLocked(p => !p)}
                                className={`lock-toggle ${locked ? 'is-locked' : 'is-unlocked'}`}
                                title={locked ? 'Odblokuj edycję' : 'Zablokuj edycję'}
                                aria-label={locked ? 'Odblokuj edycję' : 'Zablokuj edycję'}
                                type="button"
                            >
                                <LockIcon locked={locked} />
                            </button> : <></>
                        }
                    </div>

                </div>

                {/* GÓRA: lewa 60% (formularz), prawa 40% (okładka) */}
                <div className="contents-add-wrapper">
                    <div className="form-column">
                        <form id="series-form" onSubmit={handleCreateSeries} className="add-content-form">
                            <label>
                                Tytuł serii
                                <input
                                    name="title"
                                    value={form.title}
                                    onChange={handleChange}
                                    placeholder="Wpisz tytuł serii"
                                    required
                                    {...inputProps}
                                />
                            </label>

                            <label>
                                Opis
                                <textarea
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    placeholder="Krótki opis serii"
                                    {...inputProps}
                                />
                            </label>

                            <label>
                                Język
                                <input
                                    name="language"
                                    value={form.language}
                                    onChange={handleChange}
                                    placeholder="Np. PL, EN…"
                                    {...inputProps}
                                />
                            </label>

                            {/* RĘCZNY URL okładki (opcjonalny) */}
                            <label>
                                Okładka (URL)
                                <input
                                    name="cover_image_url"
                                    value={form.cover_image_url}
                                    onChange={handleChange}
                                    placeholder="https://adres-okładki..."
                                    {...inputProps}
                                />
                            </label>

                            <div className="toggle-row">
                                <label className={`violet-toggle ${locked ? 'is-disabled' : ''}`}>
                                    <input
                                        type="checkbox"
                                        name="is_premium"
                                        checked={form.is_premium}
                                        onChange={handleChange}
                                        disabled={locked}
                                    />
                                    <span className="vt-track" aria-hidden="true"><span className="vt-thumb" /></span>
                                    <span className="vt-text">
                                        Premium <span className="vt-state">{form.is_premium ? 'TAK' : 'NIE'}</span>
                                    </span>
                                </label>

                                <label className={`violet-toggle ${locked ? 'is-disabled' : ''}`}>
                                    <input
                                        type="checkbox"
                                        name="is_visible"
                                        checked={form.is_visible}
                                        onChange={handleChange}
                                        disabled={locked}
                                    />
                                    <span className="vt-track" aria-hidden="true"><span className="vt-thumb" /></span>
                                    <span className="vt-text">
                                        Widoczna <span className="vt-state">{form.is_visible ? 'TAK' : 'NIE'}</span>
                                    </span>
                                </label>
                            </div>
                        </form>
                    </div>

                    <div className="media-column">
                        {!seriesId ? (
                            <div className="add-info">Zapisz serię, aby dodać okładkę.</div>
                        ) : (
                            <>
                                {/* OKŁADKA */}
                                <div className="upload-section">
                                    <h3>Okładka serii</h3>
                                    {coverDisplay && (
                                        <div className="cover-preview">
                                            <img src={coverDisplay} alt="Okładka serii" className="cover-img" />
                                        </div>
                                    )}

                                    <div className="file-picker">
                                        <input
                                            ref={coverInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleCoverUpload}
                                            disabled={locked || !seriesId}
                                            className="file-input-hidden"
                                        />
                                        <button
                                            type="button"
                                            className="btn-file"
                                            onClick={() => coverInputRef.current?.click()}
                                            disabled={locked || !seriesId}
                                        >
                                            Wybierz obraz
                                        </button>
                                        {coverUrl && (
                                            <button
                                                type="button"
                                                className="delete-btn"
                                                onClick={handleDeleteCover}
                                                disabled={locked}
                                                style={{ marginLeft: 8 }}
                                            >
                                                Usuń okładkę
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* DÓŁ: DODAWANIE DO SERII + LISTA/UKŁADANIE */}
                {seriesId && (
                    <div className="series-bottom">
                        {/* Dodawanie nowych elementów */}
                        <div className="upload-section">
                            <h3>Dodaj treści do serii</h3>

                            <div className="autocomplete-wrapper">
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Szukaj po ID lub tytule…"
                                    disabled={locked}
                                />
                                {suggestions.length > 0 && (
                                    <ul className="autocomplete-list">
                                        {suggestions.map(s => (
                                            <li
                                                key={s.id}
                                                className="autocomplete-item"
                                                onClick={() => addToSelection(s)}
                                            >
                                                <strong>#{s.id}</strong> — {s.title}
                                                {s.language ? ` • ${s.language}` : ''} • {s.type}
                                                <span style={{ marginLeft: 8 }}>
                                                    <span className={`badge ${s.is_visible ? 'badge-success' : 'badge-muted'}`}>{s.is_visible ? 'WIDOCZNE' : 'UKRYTE'}</span>
                                                </span>
                                                <span style={{ marginLeft: 6 }}>
                                                    <span className={`badge ${s.is_premium ? 'badge-success' : 'badge-muted'}`}>{s.is_premium ? 'PREMIUM' : 'FREE'}</span>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* Wybrane do dodania */}
                            {selected.length > 0 && (
                                <div className="chips-section">
                                    <div className="chips-title">Do dodania</div>
                                    <ul className="chip-list">
                                        {selected.map(s => (
                                            <li key={s.id} className="chip">
                                                #{s.id} {s.title}
                                                <button
                                                    type="button"
                                                    className="chip-remove"
                                                    onClick={() => removeFromSelection(s.id)}
                                                    title="Usuń"
                                                    aria-label="Usuń"
                                                    disabled={locked}
                                                >
                                                    ×
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="form-buttons" style={{ marginTop: 8 }}>
                                        <button type="button" onClick={addSelectedToSeries} disabled={locked}>Dodaj do serii</button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Lista elementów serii */}
                        <div className="upload-section" style={{ marginTop: 16 }}>
                            <h3>Elementy serii</h3>
                            {sortedItems.length === 0 ? (
                                <div className="add-info">Brak elementów.</div>
                            ) : (
                                <div className="table-scroll">
                                    <table className="contents-table">
                                        <thead>
                                            <tr>
                                                <th className="col-id">#</th>
                                                <th className="col-id">ID treści</th>
                                                <th className="col-title">Tytuł</th>
                                                <th className="col-type">Typ</th>
                                                <th className="col-lang">Język</th>
                                                <th>Widoczność</th>
                                                <th>Premium</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedItems.map((it, i) => (
                                                <tr key={it.content_id}>
                                                    <td>{it.sort_index}</td>
                                                    <td className="col-id-val">{it.content_id}</td>
                                                    <td className="ellipsis" title={it.title}>{it.title}</td>
                                                    <td>{it.type}</td>
                                                    <td>{it.language || '—'}</td>
                                                    <td className="strong">
                                                        <span className={`badge ${it.is_visible ? 'badge-success' : 'badge-muted'}`}>
                                                            {it.is_visible ? 'TAK' : 'NIE'}
                                                        </span>
                                                    </td>
                                                    <td className="strong">
                                                        <span className={`badge ${it.is_premium ? 'badge-success' : 'badge-muted'}`}>
                                                            {it.is_premium ? 'TAK' : 'NIE'}
                                                        </span>
                                                    </td>
                                                    <td className="actions-cell">
                                                        <button
                                                            className="btn btn-ghost"
                                                            onClick={() => moveItem(it.content_id, -1)}
                                                            disabled={locked || i === 0}
                                                            title="Przesuń w górę"
                                                        >
                                                            ▲
                                                        </button>
                                                        <button
                                                            className="btn btn-ghost"
                                                            onClick={() => moveItem(it.content_id, +1)}
                                                            disabled={locked || i === sortedItems.length - 1}
                                                            title="Przesuń w dół"
                                                        >
                                                            ▼
                                                        </button>
                                                        <button
                                                            className="icon-btn delete"
                                                            onClick={() => askRemoveItem(it)}
                                                            aria-label="Usuń z serii"
                                                            disabled={locked}
                                                        >
                                                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M3 6h18"></path>
                                                                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                                            </svg>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* BLOK: TŁUMACZENIA OPISU SERII */}
            {seriesId && (
                <div className="translations-card">
                    <div className="tr-header">
                        <h3>Tłumaczenia opisu</h3>
                        <button
                            className="btn-ghost"
                            type="button"
                            onClick={() => setTrAddOpen(v => !v)}
                            disabled={locked}
                        >
                            {trAddOpen ? 'Zamknij' : 'Dodaj tłumaczenie'}
                        </button>
                    </div>

                    {trAddOpen && (
                        <div className="translation-form">
                            <label>
                                Język tłumaczenia
                                <select
                                    value={trLang}
                                    onChange={e => setTrLang(ensureLang(e.target.value))}
                                    disabled={locked}
                                >
                                    {ALLOWED.map(l => <option key={l} value={l}>{l}</option>)}
                                </select>
                            </label>

                            <label>
                                Opis tłumaczenia
                                <textarea
                                    placeholder="Wpisz przetłumaczony opis serii…"
                                    value={trDesc}
                                    onChange={e => setTrDesc(e.target.value)}
                                    disabled={locked}
                                />
                            </label>

                            <label className={`violet-toggle ${locked ? 'is-disabled' : ''}`}>
                                <input
                                    type="checkbox"
                                    name="tr_is_visible"
                                    checked={trVisible}
                                    onChange={e => setTrVisible(e.target.checked)}
                                    disabled={locked}
                                />
                                <span className="vt-track" aria-hidden="true"><span className="vt-thumb" /></span>
                                <span className="vt-text">
                                    Widoczna <span className="vt-state">{trVisible ? 'TAK' : 'NIE'}</span>
                                </span>
                            </label>


                            <div className="form-buttons">
                                <button type="button" onClick={handleAddTranslation} disabled={locked}>
                                    Zapisz tłumaczenie
                                </button>
                            </div>
                        </div>
                    )}

                    {translations.length === 0 ? (
                        <div className="translations-empty">Brak tłumaczeń.</div>
                    ) : (
                        <ul className="tr-accordion">
                            {translations.map(tr => {
                                const lang = ensureLang(tr.language_code);
                                const open = !!trOpen[lang];
                                const isEditing = trEditLang === lang;

                                return (
                                    <li key={lang} className={`tr-item ${open ? 'open' : ''}`}>
                                        <button type="button" className="tr-toggle" onClick={() => toggleTrOpen(lang)}>
                                            <span className="tr-lang">
                                                {lang} {tr.is_visible ? '' : '(ukryte)'}
                                            </span>
                                            <span className="tr-meta">
                                                Ostatnia edycja: {tr.last_edited_at ? new Date(tr.last_edited_at).toLocaleString() : '—'}
                                            </span>
                                        </button>

                                        {open && (
                                            <div className="tr-body">
                                                {!isEditing ? (
                                                    <div className="tr-text">
                                                        <div className="actions-row gap-8 mt-6">
                                                            <button className="btn-ghost" onClick={() => startEditTr(lang, tr.translated_description)} disabled={locked}>Edytuj</button>
                                                            <button className="btn-ghost" onClick={() => toggleTrVisibility(lang, tr.is_visible)} disabled={locked}>
                                                                {tr.is_visible ? 'Ukryj' : 'Pokaż'}
                                                            </button>
                                                            <button className="delete-btn" onClick={() => removeTranslation(lang)} disabled={locked}>Usuń tłumaczenie</button>
                                                        </div>
                                                        {tr.translated_description ? (
                                                            <div className="pre-wrap">{tr.translated_description}</div>
                                                        ) : (
                                                            <span className="muted">Brak opisu.</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="tr-text-edit">
                                                        <textarea
                                                            value={trEditDesc}
                                                            onChange={e => setTrEditDesc(e.target.value)}
                                                            disabled={locked}
                                                        />
                                                        <div className="actions-row mt-6">
                                                            <button className="btn-primary" onClick={saveEditTr} disabled={locked}>Zapisz</button>
                                                            <button className="btn-ghost" onClick={() => { setTrEditLang(null); setTrEditDesc(''); }}>Anuluj</button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}

            {/* Dialogi */}
            <Dialog open={dlgOpen} title={dlgTitle} message={dlgMsg} variant={dlgVariant} onClose={() => setDlgOpen(false)} />
            <ConfirmDialog
                open={confirmState.open}
                title={confirmState.title}
                message={confirmState.message}
                confirmLabel={confirmState.confirmLabel}
                cancelLabel={confirmState.cancelLabel}
                danger={confirmState.danger}
                onConfirm={confirmState.onConfirm || (() => setConfirmState(s => ({ ...s, open: false })))}
                onCancel={() => setConfirmState(s => ({ ...s, open: false }))}
            />
        </>
    );
}

/* większa ikona kłódki */
function LockIcon({ locked }: { locked: boolean }) {
    return locked ? (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="4" y="10" width="16" height="10" rx="2"></rect>
            <path d="M8 10V7a4 4 0 0 1 8 0v3"></path>
        </svg>
    ) : (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="4" y="11" width="16" height="9" rx="2"></rect>
            <path d="M8 11V7a 4 4 0 0 1 7.5-2"></path>
        </svg>
    );
}
